// Phase 23 (23-1 / 23-8) — /admin/agreements operator template editor.
//
// Manages the versioned Client / IC agreement templates the eligibility gates
// read. Lifecycle: create a DRAFT → publish (DRAFT → ACTIVE, superseding the
// prior ACTIVE of that type) → deprecate (DRAFT only; an ACTIVE template can't
// be deprecated — that would fail-open the gate, audit H1). `bodyHtml` is
// operator-authored DATA, sanitized on ingest (audit H2), so the reconciled
// legal text swaps in with no deploy.
//
// Destructive actions (publish / discard) route through <DangerButton>; the
// create + those endpoints are TOTP-fresh guarded server-side, and the global
// TotpStepUpModalProvider intercepts + auto-retries after step-up.

import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unwrap, messageOf, isStepUpError } from '@truecare/api-client';
import { Alert, Badge, Button, Card, CardHeader, Field, Input, useFocusTrap } from '@truecare/ui';
import { api } from '../api';
import { AdminLayout } from '../components/AdminLayout';
import { AdminTable, type AdminTableColumn } from '../components/AdminTable';
import { DangerButton } from '../components/DangerButton';
import { adminBeforeLoad } from '../lib/admin-before-load';

export const Route = createFileRoute('/agreements')({
  beforeLoad: () => adminBeforeLoad('/agreements'),
  component: AgreementsPage,
});

type AgreementType = 'CAREGIVER_IC' | 'HOMECARE_CLIENT';
type TemplateStatus = 'DRAFT' | 'ACTIVE' | 'DEPRECATED';

interface TemplateRow {
  id: string;
  type: AgreementType;
  version: string;
  title: string;
  status: TemplateStatus;
  contentHash: string;
  requiresReacceptance: boolean;
  effectiveFrom: string | null;
  supersededAt: string | null;
  createdAt: string;
  updatedAt: string;
}
interface TemplateListResponse {
  items: TemplateRow[];
  totalCount: number;
  page: number;
  pageSize: number;
}
interface TemplateDetail extends TemplateRow {
  bodyHtml: string;
}
interface ChangeLogRow {
  id: string;
  templateId: string;
  action: string;
  actorEmail: string;
  reason: string | null;
  createdAt: string;
}
interface ChangeLogResponse {
  items: ChangeLogRow[];
  totalCount: number;
  page: number;
  pageSize: number;
}

const TYPE_LABEL: Record<AgreementType, string> = {
  CAREGIVER_IC: 'Care Professional — Independent Contractor',
  HOMECARE_CLIENT: 'Homecare Client',
};
const STATUS_TONE: Record<TemplateStatus, 'warning' | 'success' | 'neutral'> = {
  DRAFT: 'warning',
  ACTIVE: 'success',
  DEPRECATED: 'neutral',
};

const TEMPLATES_KEY = ['admin', 'agreements', 'templates'];
const CHANGELOG_KEY = ['admin', 'agreements', 'change-log'];

function AgreementsPage() {
  const qc = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<AgreementType | ''>('');
  const [statusFilter, setStatusFilter] = useState<TemplateStatus | ''>('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [logPage, setLogPage] = useState(1);
  const PAGE_SIZE = 100;
  const LOG_PAGE_SIZE = 50;

  const listQuery = useQuery({
    queryKey: [...TEMPLATES_KEY, { typeFilter, statusFilter, page }],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/admin/agreements/templates' as never, {
          params: {
            query: {
              ...(typeFilter ? { type: typeFilter } : {}),
              ...(statusFilter ? { status: statusFilter } : {}),
              page: String(page),
              pageSize: String(PAGE_SIZE),
            },
          },
        } as never),
      ) as Promise<TemplateListResponse>,
  });

  const changeLogQuery = useQuery({
    queryKey: [...CHANGELOG_KEY, { logPage }],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/admin/agreements/templates/change-log' as never, {
          params: { query: { page: String(logPage), pageSize: String(LOG_PAGE_SIZE) } },
        } as never),
      ) as Promise<ChangeLogResponse>,
  });

  function invalidateAll() {
    void qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
    void qc.invalidateQueries({ queryKey: CHANGELOG_KEY });
  }

  const publishMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      unwrap(
        api.POST('/api/v1/admin/agreements/templates/{id}/publish' as never, {
          params: { path: { id } },
          body: { reason },
        } as never),
      ),
    onSuccess: invalidateAll,
  });

  const deprecateMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      unwrap(
        api.POST('/api/v1/admin/agreements/templates/{id}/deprecate' as never, {
          params: { path: { id } },
          body: { reason },
        } as never),
      ),
    onSuccess: invalidateAll,
  });

  const columns: AdminTableColumn<TemplateRow>[] = [
    {
      key: 'type',
      header: 'Type',
      width: 'w-28',
      render: (r) => (
        <span className="text-xs font-medium">
          {r.type === 'CAREGIVER_IC' ? 'IC (Professional)' : 'Client (Homecare)'}
        </span>
      ),
    },
    { key: 'version', header: 'Version', width: 'w-32' },
    {
      key: 'status',
      header: 'Status',
      width: 'w-28',
      render: (r) => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>,
    },
    { key: 'title', header: 'Title', sortable: false },
    {
      key: 'requiresReacceptance',
      header: 'Re-accept',
      width: 'w-20',
      sortable: false,
      render: (r) => (r.requiresReacceptance ? 'Yes' : 'No'),
    },
    {
      key: 'createdAt',
      header: 'Created',
      width: 'w-24',
      render: (r) => new Date(r.createdAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: 'Actions',
      // Reserve room for the 3 DRAFT buttons in one row so the flexing Title
      // column can't squeeze them off the edge.
      width: 'w-60',
      sortable: false,
      render: (r) =>
        r.status === 'DRAFT' ? (
          // A DRAFT is editable. Text buttons matching the rest of the admin;
          // all three stay on ONE row (flex-nowrap) — the table scrolls
          // horizontally on narrow screens rather than wrapping the actions.
          // Publish/Discard keep the type-the-version + reason confirm (audit #2).
          <div className="flex flex-nowrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setEditId(r.id)}
              data-testid={`edit-${r.version}`}
            >
              Edit
            </Button>
            <DangerButton
              entityName={r.version}
              actionLabel={`Publish ${r.type === 'CAREGIVER_IC' ? 'IC' : 'Client'} agreement ${r.version}`}
              onConfirm={({ reason }) =>
                publishMut.mutateAsync({ id: r.id, reason: reason ?? '' })
              }
              data-testid={`publish-${r.version}`}
            >
              Publish
            </DangerButton>
            <DangerButton
              entityName={r.version}
              actionLabel={`Discard ${r.type === 'CAREGIVER_IC' ? 'IC' : 'Client'} draft ${r.version}`}
              onConfirm={({ reason }) =>
                deprecateMut.mutateAsync({ id: r.id, reason: reason ?? '' })
              }
              data-testid={`discard-${r.version}`}
            >
              Discard
            </DangerButton>
          </div>
        ) : (
          // ACTIVE / DEPRECATED are immutable — view only.
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setDetailId(r.id)}
              data-testid={`view-${r.version}`}
            >
              View
            </Button>
          </div>
        ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl text-forest md:text-4xl">
              Agreements
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-ink-muted">
              Versioned legal templates the eligibility gates enforce — the
              Independent-Contractor agreement (Care Professionals) and the
              Client Agreement (homecare families). Create a draft, review it,
              then publish to make it the version new signers must accept.
              Publishing a version that requires re-acceptance re-gates existing
              signers.
            </p>
          </div>
          <Button type="button" variant="primary" onClick={() => setShowCreate((v) => !v)}>
            {showCreate ? 'Close' : 'New draft'}
          </Button>
        </header>

        {showCreate && (
          <CreateDraftForm
            onDone={() => {
              setShowCreate(false);
              invalidateAll();
            }}
          />
        )}

        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <SelectFilter
              label="Type"
              value={typeFilter}
              onChange={(v) => setTypeFilter(v as AgreementType | '')}
              options={[
                { value: '', label: 'All types' },
                { value: 'CAREGIVER_IC', label: 'IC (Professional)' },
                { value: 'HOMECARE_CLIENT', label: 'Client (Homecare)' },
              ]}
            />
            <SelectFilter
              label="Status"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as TemplateStatus | '')}
              options={[
                { value: '', label: 'All' },
                { value: 'DRAFT', label: 'Draft' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'DEPRECATED', label: 'Deprecated' },
              ]}
            />
          </div>

          {(publishMut.isError && !isStepUpError(publishMut.error)) && (
            <Alert tone="danger">{messageOf(publishMut.error, 'Publish failed.')}</Alert>
          )}
          {(deprecateMut.isError && !isStepUpError(deprecateMut.error)) && (
            <Alert tone="danger">{messageOf(deprecateMut.error, 'Discard failed.')}</Alert>
          )}

          <AdminTable
            columns={columns}
            rows={listQuery.data?.items ?? []}
            isLoading={listQuery.isLoading}
            isError={listQuery.isError}
            onRetry={() => void listQuery.refetch()}
            emptyMessage="No templates yet — create a draft to get started."
            page={page}
            limit={PAGE_SIZE}
            totalCount={listQuery.data?.totalCount ?? 0}
            onPageChange={setPage}
          />
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl text-forest">Change log</h2>
          <AdminTable
            columns={changeLogColumns}
            rows={changeLogQuery.data?.items ?? []}
            isLoading={changeLogQuery.isLoading}
            emptyMessage="No template changes recorded yet."
            page={logPage}
            limit={LOG_PAGE_SIZE}
            totalCount={changeLogQuery.data?.totalCount ?? 0}
            onPageChange={setLogPage}
          />
        </section>
      </div>

      {detailId && (
        <TemplateDetailModal id={detailId} onClose={() => setDetailId(null)} />
      )}
      {editId && (
        <TemplateEditModal
          id={editId}
          onClose={() => setEditId(null)}
          onSaved={invalidateAll}
        />
      )}
    </AdminLayout>
  );
}

const changeLogColumns: AdminTableColumn<ChangeLogRow>[] = [
  {
    key: 'createdAt',
    header: 'When',
    width: 'w-40',
    render: (r) => new Date(r.createdAt).toLocaleString(),
  },
  {
    key: 'action',
    header: 'Action',
    width: 'w-32',
    render: (r) => <Badge tone="neutral">{r.action}</Badge>,
  },
  { key: 'actorEmail', header: 'Operator', width: 'w-56' },
  {
    key: 'reason',
    header: 'Reason',
    sortable: false,
    render: (r) => r.reason ?? '—',
  },
];

// ─── Create-draft form ───────────────────────────────────────────────────────

function CreateDraftForm({ onDone }: { onDone: () => void }) {
  const [type, setType] = useState<AgreementType>('CAREGIVER_IC');
  const [version, setVersion] = useState('');
  const [title, setTitle] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [requiresReacceptance, setRequiresReacceptance] = useState(true);

  const create = useMutation({
    mutationFn: (body: {
      type: AgreementType;
      version: string;
      title: string;
      bodyHtml: string;
      requiresReacceptance: boolean;
    }) =>
      unwrap(
        api.POST('/api/v1/admin/agreements/templates' as never, {
          body,
        } as never),
      ),
    onSuccess: onDone,
  });

  const versionOk = /^v\d+-\d{4}-\d{2}$/.test(version);
  const canSubmit =
    versionOk && title.trim().length >= 3 && bodyHtml.trim().length > 0 && !create.isPending;

  return (
    <Card padding="lg">
      <CardHeader
        title="New agreement draft"
        description="Drafts are inert until published. HTML is sanitized on save — headings, paragraphs, lists, and links survive; scripts and styles are stripped."
      />
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <SelectFilter
          label="Agreement type"
          value={type}
          onChange={(v) => setType(v as AgreementType)}
          options={[
            { value: 'CAREGIVER_IC', label: TYPE_LABEL.CAREGIVER_IC },
            { value: 'HOMECARE_CLIENT', label: TYPE_LABEL.HOMECARE_CLIENT },
          ]}
        />
        <Field
          label="Version"
          error={version.length > 0 && !versionOk ? 'Format: v{n}-{YYYY-MM}, e.g. v2-2026-09' : undefined}
        >
          <Input
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="v1-2026-08"
          />
        </Field>
      </div>
      <Field label="Title" className="mt-4">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="TrueCare Independent Contractor Agreement"
        />
      </Field>
      <Field label="Body (HTML)" className="mt-4">
        <textarea
          rows={14}
          value={bodyHtml}
          onChange={(e) => setBodyHtml(e.target.value)}
          className="w-full rounded-md border border-border bg-cream p-3 font-mono text-xs leading-relaxed"
          placeholder="<h1>Agreement</h1>&#10;<p>…</p>"
          data-testid="agreement-body-input"
        />
      </Field>
      <label className="mt-4 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={requiresReacceptance}
          onChange={(e) => setRequiresReacceptance(e.target.checked)}
        />
        Publishing this version forces existing signers to re-accept (uncheck for
        a minor, non-material revision)
      </label>

      {create.isError && !isStepUpError(create.error) && (
        <Alert tone="danger" className="mt-4">
          {messageOf(create.error, 'Could not create the draft.')}
        </Alert>
      )}

      <div className="mt-5 flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={!canSubmit}
          loading={create.isPending}
          onClick={() =>
            create.mutate({ type, version, title: title.trim(), bodyHtml, requiresReacceptance })
          }
          data-testid="agreement-create-submit"
        >
          Create draft
        </Button>
      </div>
    </Card>
  );
}

// ─── Detail modal (body preview) ─────────────────────────────────────────────

function TemplateDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useQuery({
    queryKey: [...TEMPLATES_KEY, 'detail', id],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/admin/agreements/templates/{id}' as never, {
          params: { path: { id } },
        } as never),
      ) as Promise<TemplateDetail>,
  });
  const d = detail.data;

  // Trap focus + Escape-to-close (matches every other admin modal).
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { active: true, onEscape: onClose });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Agreement template detail"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-md border border-border bg-cream p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl text-forest">
              {d ? d.title : 'Loading…'}
            </h2>
            {d && (
              <p className="mt-1 text-xs text-ink-muted">
                {TYPE_LABEL[d.type]} · {d.version} ·{' '}
                <Badge tone={STATUS_TONE[d.status]}>{d.status}</Badge> · hash{' '}
                <span className="font-mono">{d.contentHash.slice(0, 12)}…</span>
              </p>
            )}
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        {d ? (
          <div
            className="mt-4 rounded-md border border-border bg-paper p-4 text-sm leading-relaxed [&_h1]:mb-2 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:mb-1 [&_h2]:mt-3 [&_h2]:font-semibold [&_li]:ml-5 [&_li]:list-disc [&_p]:mb-3"
            // Already-sanitized template body (audit H2 strips at ingest).
            dangerouslySetInnerHTML={{ __html: d.bodyHtml }}
          />
        ) : (
          <p className="mt-4 text-sm text-ink-muted">Loading agreement body…</p>
        )}
      </div>
    </div>
  );
}

// ─── Edit modal (DRAFT only — editable title + body + live preview) ──────────
function TemplateEditModal({
  id,
  onClose,
  onSaved,
}: {
  id: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const detail = useQuery({
    queryKey: [...TEMPLATES_KEY, 'detail', id],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/admin/agreements/templates/{id}' as never, {
          params: { path: { id } },
        } as never),
      ) as Promise<TemplateDetail>,
  });
  const d = detail.data;
  const [title, setTitle] = useState<string | null>(null);
  const [bodyHtml, setBodyHtml] = useState<string | null>(null);
  const [reAccept, setReAccept] = useState(true);
  // Audit H13 (2026-09-01) — the live preview renders operator-pasted (counsel-
  // provided) HTML that is only sanitized server-side ON SAVE. Rendering it via
  // dangerouslySetInnerHTML executed inline handlers (e.g. <img onerror=…>) in
  // the authenticated admin origin, inside the 15-min TOTP-fresh window that
  // gates the most destructive actions — a self-XSS. Render it instead in a
  // fully sandboxed iframe (sandbox="" ⇒ scripts + inline handlers disabled, no
  // same-origin cookie access), so nothing in the preview can execute or exfil.
  const previewSrcDoc = useMemo(
    () =>
      `<!doctype html><html><head><meta charset="utf-8"><style>` +
      `html,body{margin:0}body{font:14px/1.6 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#2b2b2b;padding:16px}` +
      `h1{font-size:1.125rem;font-weight:600;margin:0 0 .5rem}h2{font-weight:600;margin:.75rem 0 .25rem}` +
      `p{margin:0 0 .75rem}li{list-style:disc;margin-left:1.25rem}a{color:#1d4ed8}` +
      `</style></head><body>${bodyHtml ?? ''}</body></html>`,
    [bodyHtml],
  );
  // Seed the editable fields once, the first time the detail loads.
  useEffect(() => {
    if (d && title === null) {
      setTitle(d.title);
      setBodyHtml(d.bodyHtml);
      setReAccept(d.requiresReacceptance);
    }
  }, [d, title]);

  const save = useMutation({
    mutationFn: (input: {
      title: string;
      bodyHtml: string;
      requiresReacceptance: boolean;
    }) =>
      unwrap(
        api.PATCH('/api/v1/admin/agreements/templates/{id}' as never, {
          params: { path: { id } },
          body: input,
        } as never),
      ),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const notDraft = d != null && d.status !== 'DRAFT';
  const canSave =
    title !== null &&
    bodyHtml !== null &&
    title.trim().length >= 3 &&
    bodyHtml.trim().length > 0 &&
    !notDraft &&
    !save.isPending;

  // Trap focus + Escape-to-close (matches every other admin modal).
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { active: true, onEscape: onClose });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit agreement template"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-md border border-border bg-cream p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl text-forest">Edit draft</h2>
            {d && (
              <p className="mt-1 text-xs text-ink-muted">
                {TYPE_LABEL[d.type]} · {d.version} ·{' '}
                <Badge tone={STATUS_TONE[d.status]}>{d.status}</Badge>
              </p>
            )}
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>

        {notDraft && (
          <Alert tone="warning" className="mt-4">
            This template is {d?.status} — only a DRAFT can be edited. Create a new
            version instead.
          </Alert>
        )}

        {title === null ? (
          <p className="mt-4 text-sm text-ink-muted">Loading…</p>
        ) : (
          <>
            <Field label="Title" className="mt-4">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="agreement-edit-title"
              />
            </Field>
            <Field label="Body (HTML)" className="mt-4">
              <textarea
                rows={14}
                value={bodyHtml ?? ''}
                onChange={(e) => setBodyHtml(e.target.value)}
                className="w-full rounded-md border border-border bg-cream p-3 font-mono text-xs leading-relaxed"
                data-testid="agreement-edit-body"
              />
            </Field>
            <div className="mt-4">
              <p className="mb-1 text-xs font-semibold text-ink-muted">Preview</p>
              {/* Sandboxed (scripts disabled, no same-origin) — see previewSrcDoc / audit H13. */}
              <iframe
                title="Agreement preview"
                sandbox=""
                srcDoc={previewSrcDoc}
                className="h-72 w-full rounded-md border border-border bg-paper"
                data-testid="agreement-edit-preview"
              />
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={reAccept}
                onChange={(e) => setReAccept(e.target.checked)}
                data-testid="agreement-edit-reaccept"
              />
              Publishing this version forces existing signers to re-accept
              (uncheck for a minor, non-material revision)
            </label>
          </>
        )}

        {save.isError && !isStepUpError(save.error) && (
          <Alert tone="danger" className="mt-4" data-testid="agreement-edit-error">
            {messageOf(save.error, 'Could not save the draft.')}
          </Alert>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => {
              if (title !== null && bodyHtml !== null) {
                save.mutate({ title: title.trim(), bodyHtml, requiresReacceptance: reAccept });
              }
            }}
            data-testid="agreement-edit-save"
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Small labelled native select ────────────────────────────────────────────

function SelectFilter({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-border bg-cream px-3 py-2 text-sm text-ink"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
